import { useState, useEffect, useRef, useCallback } from 'react';
import logger from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Search, Package2, Edit, Trash2, Loader2, Scissors, BarChart3, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navbar from '@/components/Navbar';
import YarnUsageModal from '@/components/YarnUsageModal';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Inventory() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const [yarns, setYarns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteYarnId, setDeleteYarnId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [usageModalOpen, setUsageModalOpen] = useState(false);
  const [selectedYarnForUsage, setSelectedYarnForUsage] = useState(null);
  const observer = useRef();
  const navigate = useNavigate();

  const lastYarnRef = useCallback(node => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setCurrentPage(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore]);

  useEffect(() => {
    setYarns([]);
    setCurrentPage(1);
    setHasMore(true);
  }, [searchTerm]);

  const loadYarns = useCallback(async () => {
    if (currentPage === 1) setLoading(true);
    else setLoadingMore(true);
    
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', '20');
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/yarns?${params.toString()}`, { withCredentials: true });
      const newItems = response.data.items || response.data;
      
      if (currentPage === 1) {
        setYarns(newItems);
      } else {
        setYarns(prev => [...prev, ...newItems]);
      }
      
      setTotalItems(response.data.total || newItems.length);
      setHasMore(currentPage < (response.data.total_pages || 1));
    } catch (error) {
      logger.error('Failed to load yarns:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentPage, searchTerm, t]);

  useEffect(() => {
    loadYarns();
  }, [loadYarns]);

  const handleDelete = async () => {
    if (!deleteYarnId) return;

    try {
      await axios.delete(`${API}/yarns/${deleteYarnId}`, {
        withCredentials: true
      });
      toast.success(t('common.success'));
      setDeleteYarnId(null);
      setYarns([]);
      setCurrentPage(1);
      setHasMore(true);
    } catch (error) {
      logger.error('Failed to delete yarn:', error);
      toast.error(t('common.error'));
    }
  };

  const openUsageModal = (yarn, e) => {
    e.stopPropagation();
    setSelectedYarnForUsage(yarn);
    setUsageModalOpen(true);
  };

  const handleUsageLogged = (data) => {
    // Update the yarn in the list with new quantity
    setYarns(prev => prev.map(y => 
      y.yarn_id === selectedYarnForUsage?.yarn_id 
        ? { ...y, quantity: data.new_quantity }
        : y
    ));
  };

  // Get the primary image for a yarn (first from images array or fallback to image_url)
  const getYarnImage = (yarn) => {
    if (yarn.images && yarn.images.length > 0) {
      return yarn.images[0];
    }
    return yarn.image_url || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-2">
              {t('inventory.title')}
            </h1>
            <p className="text-lg text-muted-foreground font-body">
              {totalItems} {totalItems === 1 ? t('inventory.yarn') : t('inventory.yarnsInStash')}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/statistics')}
              className="rounded-full"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              {isEnglish ? 'Statistics' : 'Tilastot'}
            </Button>
            <Button
              data-testid="add-yarn-button"
              onClick={() => navigate('/inventory/add')}
              className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('inventory.addYarn')}
            </Button>
          </div>
        </div>

        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              data-testid="search-input"
              type="text"
              placeholder={t('inventory.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
            />
          </div>
        </div>

        {yarns.length === 0 ? (
          <div className="text-center py-20">
            <Package2 className="h-24 w-24 text-muted-foreground/30 mx-auto mb-6" />
            <h3 className="text-2xl font-heading font-semibold mb-2">{t('inventory.noYarns')}</h3>
            <p className="text-muted-foreground font-body mb-6">
              {searchTerm ? t('inventory.noMatch') : t('inventory.noYarnsMessage')}
            </p>
            {!searchTerm && (
              <Button
                data-testid="empty-add-yarn-button"
                onClick={() => navigate('/inventory/add')}
                className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('dashboard.addFirstYarn')}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {yarns.map((yarn, index) => {
                const image = getYarnImage(yarn);
                const hasMultipleImages = yarn.images && yarn.images.length > 1;
                
                return (
                  <div
                    key={yarn.yarn_id}
                    ref={index === yarns.length - 1 ? lastYarnRef : null}
                    data-testid={`yarn-card-${yarn.yarn_id}`}
                    className="group relative rounded-3xl overflow-hidden bg-white border-4 border-white shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 hover:rotate-1 cursor-pointer"
                  >
                    <div
                      onClick={() => navigate(`/inventory/${yarn.yarn_id}`)}
                      className="aspect-square bg-gradient-to-br from-rose-100 to-orange-100 flex items-center justify-center relative overflow-hidden"
                    >
                      {image ? (
                        <img 
                          src={image} 
                          alt={`${yarn.brand} - ${yarn.color}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package2 className="h-16 w-16 text-primary" />
                      )}
                      {/* Quantity badge */}
                      {yarn.quantity > 1 && (
                        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-primary text-white text-xs font-bold">
                          ×{yarn.quantity}
                        </span>
                      )}
                      {/* Multiple images indicator */}
                      {hasMultipleImages && (
                        <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-xs">
                          +{yarn.images.length - 1}
                        </span>
                      )}
                    </div>
                    <div className="p-4 bg-white">
                      <h3 className="font-heading font-semibold text-lg mb-1 truncate">
                        {yarn.brand || (isEnglish ? 'Unknown Brand' : 'Tuntematon')}
                      </h3>
                      <p className="text-sm text-muted-foreground font-body truncate mb-2">
                        {yarn.color || (isEnglish ? 'Unknown Color' : 'Tuntematon')}
                      </p>
                      <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                        <span className="text-xs font-medium text-stone-500">
                          {yarn.weight || 'N/A'}
                        </span>
                        <div className="flex items-center space-x-1">
                          {/* Mark as Used button */}
                          {yarn.quantity > 0 && (
                            <Button
                              data-testid={`use-yarn-${yarn.yarn_id}`}
                              size="sm"
                              variant="ghost"
                              onClick={(e) => openUsageModal(yarn, e)}
                              className="h-8 w-8 p-0 hover:bg-green-50"
                              title={isEnglish ? 'Mark as Used' : 'Merkitse käytetyksi'}
                            >
                              <Scissors className="h-4 w-4 text-stone-500 hover:text-green-600" />
                            </Button>
                          )}
                          {/* Find Patterns button */}
                          <Button
                            data-testid={`patterns-yarn-${yarn.yarn_id}`}
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/inventory/${yarn.yarn_id}?tab=patterns`);
                            }}
                            className="h-8 w-8 p-0 hover:bg-blue-50"
                            title={isEnglish ? 'Find Patterns' : 'Etsi ohjeita'}
                          >
                            <Lightbulb className="h-4 w-4 text-stone-500 hover:text-blue-600" />
                          </Button>
                          <Button
                            data-testid={`edit-yarn-${yarn.yarn_id}`}
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/inventory/${yarn.yarn_id}/edit`);
                            }}
                            className="h-8 w-8 p-0 hover:bg-rose-50"
                          >
                            <Edit className="h-4 w-4 text-stone-500 hover:text-primary" />
                          </Button>
                          <Button
                            data-testid={`delete-yarn-${yarn.yarn_id}`}
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteYarnId(yarn.yarn_id);
                            }}
                            className="h-8 w-8 p-0 hover:bg-rose-50"
                          >
                            <Trash2 className="h-4 w-4 text-stone-500 hover:text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {loadingMore && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
            
            {!hasMore && yarns.length > 0 && (
              <p className="text-center text-muted-foreground py-8">
                {isEnglish ? `You've seen all ${totalItems} yarns` : `Olet nähnyt kaikki ${totalItems} lankaa`}
              </p>
            )}
          </>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteYarnId} onOpenChange={() => setDeleteYarnId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('inventory.delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('inventory.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">{t('inventory.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('inventory.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Yarn Usage Modal */}
      <YarnUsageModal
        isOpen={usageModalOpen}
        onClose={() => {
          setUsageModalOpen(false);
          setSelectedYarnForUsage(null);
        }}
        yarn={selectedYarnForUsage}
        onUsageLogged={handleUsageLogged}
      />
    </div>
  );
}
